/**
 * Exhibitor list cards read `LOGO` from `getExhibitorList`. Some shows return `LOGO` empty
 * while `getExhibitor` still has `EXHIBITOR.DIRECTORY.LOGO`. This module fills gaps via
 * `getExhibitor`, caches results, dedupes concurrent calls, and lets the parent prefetch
 * the current page in parallel (see ExhibitorSearch / ExhibitorFeatured).
 *
 * Backend (to fix at source): ensure `getExhibitorList` → `EXHIBITORS[].LOGO` is populated
 * when directory has a logo (same source as detail). Endpoint shape:
 *   {ESHOW_BASE}/webservices/eshow/floor_space.cfc?method=getExhibitorList&token=...
 * Workaround uses:
 *   ...?method=getExhibitor&exhibitor_key={EXHIBITOR_KEY}&token=...
 */
import axiosInstance from './axiosInstance.js';
import { ESHOW_URL } from '../config.js';
import { assetUrl, getTokenCookie, getTokenPlanner } from './Common.js';

/** Raw logo URL from getExhibitor → EXHIBITOR.DIRECTORY.LOGO */
export const exhibitorLogoFallbackCache = new Map();

/** One in-flight Promise per EXHIBITOR_KEY so prefetch and card do not double-fetch */
const logoFetchInFlight = new Map();

export function getListLogoFromItem(item) {
  return (item?.LOGO || item?.DIRECTORY?.LOGO || '').trim();
}

export function getTokenForEshowExhibitorApi(main) {
  try {
    let token = getTokenPlanner();
    if (main?.authInfo?.role === 'admin') {
      token = getTokenCookie();
    } else if (!token) {
      token = getTokenCookie();
    }
    return token;
  } catch (e) {
    return getTokenCookie();
  }
}

function preloadLogoForDisplay(rawUrl) {
  if (!rawUrl || typeof window === 'undefined') return;
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = assetUrl(rawUrl);
  } catch (e) {
    // ignore
  }
}

/** Loads logo via getExhibitor; fills cache; dedupes parallel callers. */
export async function ensureExhibitorLogoCached(exhibitorKey, main) {
  if (!exhibitorKey) return null;
  if (exhibitorLogoFallbackCache.has(exhibitorKey)) {
    return exhibitorLogoFallbackCache.get(exhibitorKey);
  }
  if (logoFetchInFlight.has(exhibitorKey)) {
    return logoFetchInFlight.get(exhibitorKey);
  }

  const p = (async () => {
    try {
      const token = getTokenForEshowExhibitorApi(main);
      const url = `${ESHOW_URL()}?method=getExhibitor&exhibitor_key=${exhibitorKey}&token=${token}`;
      const res = await axiosInstance.get(url);
      const logo = (res.data?.EXHIBITOR?.DIRECTORY?.LOGO || '').trim();
      if (logo) {
        exhibitorLogoFallbackCache.set(exhibitorKey, logo);
        preloadLogoForDisplay(logo);
      }
      return logo || null;
    } catch (e) {
      return null;
    } finally {
      logoFetchInFlight.delete(exhibitorKey);
    }
  })();

  logoFetchInFlight.set(exhibitorKey, p);
  return p;
}

/**
 * Prefetch missing logos for a page of items; runs in waves of `concurrency` parallel requests.
 * @returns {Promise<boolean>} true if any keys were fetched (caller may bump UI sync version)
 */
export async function prefetchExhibitorLogosForItems(items, main, { concurrency = 8 } = {}) {
  const keys = [
    ...new Set(
      items
        .filter((it) => !getListLogoFromItem(it) && it.EXHIBITOR_KEY)
        .map((it) => it.EXHIBITOR_KEY)
    ),
  ].filter((k) => !exhibitorLogoFallbackCache.has(k));
  if (keys.length === 0) return false;

  for (let i = 0; i < keys.length; i += concurrency) {
    const chunk = keys.slice(i, i + concurrency);
    await Promise.all(chunk.map((key) => ensureExhibitorLogoCached(key, main)));
  }
  return true;
}
